import { useMemo, useCallback } from "react";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";

export default function TransLocationSelect({
  locations,
  promoData,
  setPromoData,
  errors,
  setErrors,
  inputRefs,
}) {
  const allIds = useMemo(() => locations?.map((l) => l.Uid) ?? [], [locations]);
  const idToName = useMemo(
    () => new Map(locations?.map((l) => [l.Uid, l.Name1]) ?? []),
    [locations]
  );

  const selected = promoData?.transLocation ?? [];
  const total = allIds.length;
  const selectedCount = selected.length;
  const isAllSelected = total > 0 && selectedCount === total;
  const isIndeterminate = selectedCount > 0 && selectedCount < total;

  const applySelection = useCallback(
    (next) => {
      setPromoData((prev) => ({ ...prev, transLocation: next }));
      setErrors((prev) => ({ ...prev, transLocation: false }));
    },
    [setPromoData, setErrors]
  );

  const handleChange = useCallback(
    (event) => {
      const value = event.target.value;

      // If user clicked "Select All" (always appended as last clicked value)
      if (value[value.length - 1] === "all") {
        applySelection(isAllSelected ? [] : allIds);
        return;
      }
      applySelection(value);
    },
    [applySelection, isAllSelected, allIds]
  );

  const renderValue = useCallback(
    (selectedIds) => {
      if (!selectedIds?.length) return "Select Trans Location";

      // Show up to 3, then "+N more"
      const names = selectedIds.map((id) => idToName.get(id) ?? id);
      const preview = names.slice(0, 3).join(", ");
      const more = names.length > 3 ? ` +${names.length - 3} more` : "";
      return `${preview}${more}`;
    },
    [idToName]
  );

  return (
    <FormControl size="small" fullWidth error={Boolean(errors?.transLocation)}>
      <InputLabel id="location-select-label" shrink>
        Promo Location
      </InputLabel>
      <Select
        labelId="location-select-label"
        id="location-select"
        multiple
        value={selected}
        inputRef={(el) => (inputRefs.current["location"] = el)}
        onChange={handleChange}
        label="Promo Location"
        displayEmpty
        renderValue={renderValue}
        MenuProps={{
          PaperProps: { style: { maxHeight: 240, width: 280 } },
        }}
      >
        <MenuItem value="all" className="menuSm">
          <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} />
          <ListItemText primary="Select All" />
        </MenuItem>

        {locations?.map((loc) => (
          <MenuItem className="menuSm" key={loc.Uid} value={loc.Uid}>
            <Checkbox checked={selected.includes(loc.Uid)} />
            <ListItemText primary={loc.Name1} />
          </MenuItem>
        ))}
      </Select>

      {errors?.transLocation && (
        <FormHelperText>Trans Location is required</FormHelperText>
      )}
    </FormControl>
  );
}
